import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { queryClient as applicationQueryClient } from "@/lib/queryClient";
import {
  clearAllUserViewStates,
  clearUserViewStates,
  downgradeViewToRouteOnly,
  setDefaultSessionStoreEvictionHandler,
  type SessionStoreOptions,
  type SessionStoreEviction,
} from "./sessionStore";
import type { StudyGroupViewKey } from "./types";
import { recordStudyGroupViewRestorationTelemetry } from "./telemetry";

const RETENTION_MS = 30 * 60 * 1_000;
const MAX_SCOPES_PER_GROUP = 5;
const MAX_EXPANDED_POSTS_PER_SCOPE = 20;
const MAX_POSTS_PER_SCOPE = 100;
const MAX_COMMENTS_PER_SCOPE = 250;
const MAX_POSTS_GLOBAL = 250;
const MAX_COMMENTS_GLOBAL = 750;

const telemetryScopeType = (viewKey: StreamViewKey) =>
  viewKey === "stream:home" ? ("home" as const) : ("channel" as const);

export type StreamViewKey = Extract<
  StudyGroupViewKey,
  "stream:home" | `stream:channel:${number}`
>;

export type RetainedQuerySnapshot = {
  queryKey: readonly unknown[];
  dataUpdatedAt: number;
  pageParams?: unknown[];
};

export type RetainedStreamDataset = {
  id: string;
  userId: number | string;
  studyGroupId: number;
  scopeKey: StreamViewKey;
  lastAccessedAt: number;
  queries: RetainedQuerySnapshot[];
  postContentIds: number[];
  commentIds: number[];
};

export type RetainedDatasetValidationRepair = {
  dataset: RetainedStreamDataset;
  removedCommentQueryKeys: QueryKey[];
};

export type RetainedDatasetQuerySet = {
  normalPosts: QueryKey;
  pinnedPosts: QueryKey;
  comments: readonly QueryKey[];
  channel?: QueryKey;
};

export type RetainStreamDatasetInput = {
  userId: number | string;
  studyGroupId: number;
  viewKey: StreamViewKey;
  queries: RetainedDatasetQuerySet;
  postContentIds: readonly number[];
  pinnedPostContentIds?: readonly number[];
  commentIds: readonly number[];
  expandedPostContentIds: readonly number[];
};

export type RetainedDatasetRegistryOptions = {
  queryClient: QueryClient;
  now?: () => number;
  createId?: () => string;
  sessionStoreOptions?: SessionStoreOptions;
};

export type DiscardRetainedDatasetOptions = {
  forceRemoveActiveQueries?: boolean;
};

type CapturedQuery = {
  snapshot: RetainedQuerySnapshot;
  queryHash: string;
};

type RegistryEntry = {
  dataset: RetainedStreamDataset;
  userKey: string;
  capturedQueries: CapturedQuery[];
};

const cloneValue = <T>(value: T): T => structuredClone(value);

const cloneDataset = (dataset: RetainedStreamDataset): RetainedStreamDataset =>
  cloneValue(dataset);

const uniquePositiveIds = (ids: readonly number[]): number[] => [
  ...new Set(ids.filter((id) => Number.isSafeInteger(id) && id > 0)),
];

const commentQueryPostContentId = (
  queryKey: readonly unknown[],
): number | null => {
  if (queryKey[0] !== "posts" || queryKey[1] !== "comments") return null;
  const postContentId = Number(queryKey[2]);
  return Number.isSafeInteger(postContentId) && postContentId > 0
    ? postContentId
    : null;
};

const getPageParams = (data: unknown): unknown[] | undefined => {
  if (typeof data !== "object" || data === null || !("pageParams" in data)) {
    return undefined;
  }
  const pageParams = (data as { pageParams?: unknown }).pageParams;
  return Array.isArray(pageParams) ? cloneValue(pageParams) : undefined;
};

type SeenObjects = {
  leftToRight: WeakMap<object, object>;
  rightToLeft: WeakMap<object, object>;
};

const bytesEqual = (left: ArrayBufferView, right: ArrayBufferView): boolean => {
  if (
    left.constructor !== right.constructor ||
    left.byteLength !== right.byteLength
  ) {
    return false;
  }
  const leftBytes = new Uint8Array(
    left.buffer,
    left.byteOffset,
    left.byteLength,
  );
  const rightBytes = new Uint8Array(
    right.buffer,
    right.byteOffset,
    right.byteLength,
  );
  return leftBytes.every((value, index) => value === rightBytes[index]);
};

const valuesExactlyEqual = (
  left: unknown,
  right: unknown,
  seen: SeenObjects = {
    leftToRight: new WeakMap(),
    rightToLeft: new WeakMap(),
  },
): boolean => {
  if (Object.is(left, right)) return true;
  if (
    typeof left !== "object" ||
    left === null ||
    typeof right !== "object" ||
    right === null ||
    Object.getPrototypeOf(left) !== Object.getPrototypeOf(right)
  ) {
    return false;
  }

  const pairedRight = seen.leftToRight.get(left);
  const pairedLeft = seen.rightToLeft.get(right);
  if (pairedRight || pairedLeft) {
    return pairedRight === right && pairedLeft === left;
  }
  seen.leftToRight.set(left, right);
  seen.rightToLeft.set(right, left);

  if (left instanceof Date && right instanceof Date) {
    return Object.is(left.getTime(), right.getTime());
  }
  if (left instanceof RegExp && right instanceof RegExp) {
    return left.source === right.source && left.flags === right.flags;
  }
  if (ArrayBuffer.isView(left) && ArrayBuffer.isView(right)) {
    return bytesEqual(left, right);
  }
  if (left instanceof ArrayBuffer && right instanceof ArrayBuffer) {
    return bytesEqual(new Uint8Array(left), new Uint8Array(right));
  }
  if (left instanceof Map && right instanceof Map) {
    if (left.size !== right.size) return false;
    const rightEntries = right.entries();
    for (const [leftKey, leftValue] of left) {
      const next = rightEntries.next();
      if (
        next.done ||
        !valuesExactlyEqual(leftKey, next.value[0], seen) ||
        !valuesExactlyEqual(leftValue, next.value[1], seen)
      ) {
        return false;
      }
    }
    return rightEntries.next().done === true;
  }
  if (left instanceof Set && right instanceof Set) {
    if (left.size !== right.size) return false;
    const rightValues = right.values();
    for (const leftValue of left) {
      const next = rightValues.next();
      if (next.done || !valuesExactlyEqual(leftValue, next.value, seen)) {
        return false;
      }
    }
    return rightValues.next().done === true;
  }

  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every(
    (key) =>
      rightKeys.includes(key) &&
      valuesExactlyEqual(
        (left as Record<string, unknown>)[key],
        (right as Record<string, unknown>)[key],
        seen,
      ),
  );
};

const pageParamsMatch = (
  current: unknown[] | undefined,
  retained: unknown[] | undefined,
): boolean => valuesExactlyEqual(current, retained);

const defaultId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `retained-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

export class RetainedDatasetRegistry {
  readonly #queryClient: QueryClient;
  readonly #now: () => number;
  readonly #createId: () => string;
  readonly #sessionStoreOptions?: SessionStoreOptions;
  readonly #entries = new Map<string, RegistryEntry>();
  readonly #queryOwnership = new Map<string, Set<string>>();

  constructor(options: RetainedDatasetRegistryOptions) {
    this.#queryClient = options.queryClient;
    this.#now = options.now ?? Date.now;
    this.#createId = options.createId ?? defaultId;
    this.#sessionStoreOptions = options.sessionStoreOptions;
  }

  retainStreamDataset(
    input: RetainStreamDatasetInput,
  ): RetainedStreamDataset | null {
    const previous = this.#entryForScope(input);
    const capturedQueries = this.#captureQueries(input);
    if (!capturedQueries) {
      this.#rejectRetention(input, previous);
      return null;
    }

    const postContentIds = uniquePositiveIds([
      ...input.postContentIds,
      ...(input.pinnedPostContentIds ?? []),
    ]);
    const commentIds = uniquePositiveIds(input.commentIds);
    const expandedPostContentIds = uniquePositiveIds(
      input.expandedPostContentIds,
    );
    if (
      postContentIds.length > MAX_POSTS_PER_SCOPE ||
      commentIds.length > MAX_COMMENTS_PER_SCOPE ||
      expandedPostContentIds.length > MAX_EXPANDED_POSTS_PER_SCOPE
    ) {
      this.#rejectRetention(input, previous);
      recordStudyGroupViewRestorationTelemetry({
        metric: "hard_limit_rejection",
        count: 1,
        scopeType: telemetryScopeType(input.viewKey),
        failure: "hard_limit",
      });
      return null;
    }

    const incomingQueryHashes = new Set(
      capturedQueries.map(({ queryHash }) => queryHash),
    );
    const dataset: RetainedStreamDataset = {
      id: this.#createId(),
      userId: input.userId,
      studyGroupId: input.studyGroupId,
      scopeKey: input.viewKey,
      lastAccessedAt: this.#now(),
      queries: capturedQueries.map(({ snapshot }) => cloneValue(snapshot)),
      postContentIds,
      commentIds,
    };
    const entry: RegistryEntry = {
      dataset,
      userKey: String(input.userId),
      capturedQueries,
    };

    const plannedEvictions = this.#planEvictions(entry, previous);
    if (!plannedEvictions) {
      this.#rejectRetention(input, previous);
      recordStudyGroupViewRestorationTelemetry({
        metric: "hard_limit_rejection",
        count: 1,
        scopeType: telemetryScopeType(input.viewKey),
        failure: "hard_limit",
      });
      return null;
    }

    if (previous) this.#evictEntry(previous, false, incomingQueryHashes);
    for (const candidate of plannedEvictions) {
      this.#evictEntry(candidate, true, incomingQueryHashes);
    }
    if (plannedEvictions.length > 0) {
      recordStudyGroupViewRestorationTelemetry({
        metric: "lru_eviction",
        count: plannedEvictions.length,
        scopeType: telemetryScopeType(input.viewKey),
        failure: "hard_limit",
      });
    }

    this.#entries.set(dataset.id, entry);
    for (const { queryHash } of capturedQueries) {
      const owners = this.#queryOwnership.get(queryHash) ?? new Set<string>();
      owners.add(dataset.id);
      this.#queryOwnership.set(queryHash, owners);
    }
    return cloneDataset(dataset);
  }

  resolveRetainedDataset(datasetId: string): RetainedStreamDataset | null {
    const entry = this.#entries.get(datasetId);
    if (!entry) return null;
    if (
      this.#now() - entry.dataset.lastAccessedAt >= RETENTION_MS ||
      !this.#snapshotsMatch(entry)
    ) {
      this.#evictEntry(entry, true);
      return null;
    }
    return cloneDataset(entry.dataset);
  }

  touchRetainedDataset(datasetId: string): boolean {
    const entry = this.#entries.get(datasetId);
    if (!entry || !this.resolveRetainedDataset(datasetId)) return false;
    entry.dataset.lastAccessedAt = this.#now();
    return true;
  }

  evictRetainedDataset(datasetId: string): boolean {
    return this.discardRetainedDataset(datasetId);
  }

  discardRetainedDataset(
    datasetId: string,
    options: DiscardRetainedDatasetOptions = {},
  ): boolean {
    const entry = this.#entries.get(datasetId);
    if (!entry) return false;
    this.#evictEntry(
      entry,
      true,
      new Set(),
      options.forceRemoveActiveQueries ?? false,
    );
    return true;
  }

  discardRetainedStreamScope(
    userId: number | string,
    studyGroupId: number,
    viewKey: StreamViewKey,
    options: DiscardRetainedDatasetOptions = {},
  ): void {
    const entries = [...this.#entries.values()].filter(
      (entry) =>
        entry.userKey === String(userId) &&
        entry.dataset.studyGroupId === studyGroupId &&
        entry.dataset.scopeKey === viewKey,
    );
    for (const entry of entries) {
      this.#evictEntry(
        entry,
        true,
        new Set(),
        options.forceRemoveActiveQueries ?? false,
      );
    }
    if (entries.length === 0) {
      downgradeViewToRouteOnly(
        userId,
        studyGroupId,
        viewKey,
        this.#sessionStoreOptions,
      );
    }
  }

  discardSessionStoreEvictions(
    evictions: readonly SessionStoreEviction[],
  ): void {
    for (const eviction of evictions) {
      const matching = [...this.#entries.values()].filter(
        (entry) =>
          entry.userKey === String(eviction.userId) &&
          entry.dataset.studyGroupId === eviction.studyGroupId &&
          entry.dataset.scopeKey === eviction.viewKey,
      );
      for (const entry of matching) this.#evictEntry(entry, false);
    }
  }

  repairAfterValidation(
    datasetId: string,
    invalidatedPostContentIds: readonly number[],
  ): RetainedDatasetValidationRepair | null {
    const entry = this.#entries.get(datasetId);
    if (!entry || !this.resolveRetainedDataset(datasetId)) return null;
    const invalidatedIds = new Set(
      uniquePositiveIds(invalidatedPostContentIds),
    );
    if (invalidatedIds.size === 0) {
      return {
        dataset: cloneDataset(entry.dataset),
        removedCommentQueryKeys: [],
      };
    }

    const retainedQueries: CapturedQuery[] = [];
    const removedCommentQueryKeys: QueryKey[] = [];
    for (const captured of entry.capturedQueries) {
      const postContentId = commentQueryPostContentId(
        captured.snapshot.queryKey,
      );
      if (postContentId === null || !invalidatedIds.has(postContentId)) {
        retainedQueries.push(captured);
        continue;
      }

      const owners = this.#queryOwnership.get(captured.queryHash);
      owners?.delete(entry.dataset.id);
      if (owners && owners.size > 0) continue;
      this.#queryOwnership.delete(captured.queryHash);

      const query = this.#queryClient.getQueryCache().find({
        queryKey: captured.snapshot.queryKey,
        exact: true,
      });
      if (
        query?.queryHash === captured.queryHash &&
        query.getObserversCount() === 0
      ) {
        this.#queryClient.removeQueries({
          queryKey: captured.snapshot.queryKey,
          exact: true,
        });
        removedCommentQueryKeys.push(cloneValue(captured.snapshot.queryKey));
      }
    }

    entry.capturedQueries = retainedQueries;
    entry.dataset.queries = retainedQueries.map(({ snapshot }) =>
      cloneValue(snapshot),
    );
    entry.dataset.postContentIds = entry.dataset.postContentIds.filter(
      (postContentId) => !invalidatedIds.has(postContentId),
    );
    return {
      dataset: cloneDataset(entry.dataset),
      removedCommentQueryKeys,
    };
  }

  clearRetainedDatasetsForUser(userId: number | string): void {
    const userKey = String(userId);
    for (const entry of [...this.#entries.values()]) {
      if (entry.userKey === userKey) this.#evictEntry(entry, false);
    }
    clearUserViewStates(userId, this.#sessionStoreOptions);
  }

  forceClearRetainedDatasetsForUser(userId: number | string): void {
    const userKey = String(userId);
    for (const entry of [...this.#entries.values()]) {
      if (entry.userKey === userKey)
        this.#evictEntry(entry, false, undefined, true);
    }
    clearUserViewStates(userId, this.#sessionStoreOptions);
  }

  forceClearAllRetainedDatasets(): void {
    for (const entry of [...this.#entries.values()]) {
      this.#evictEntry(entry, false, undefined, true);
    }
    clearAllUserViewStates(this.#sessionStoreOptions);
  }

  #captureQueries(input: RetainStreamDatasetInput): CapturedQuery[] | null {
    if (
      input.viewKey !== "stream:home" &&
      (!input.queries.channel || input.queries.channel.length === 0)
    ) {
      return null;
    }
    const queryKeys: QueryKey[] = [
      input.queries.normalPosts,
      input.queries.pinnedPosts,
      ...input.queries.comments,
      ...(input.queries.channel ? [input.queries.channel] : []),
    ];
    const capturedByHash = new Map<string, CapturedQuery>();
    for (const queryKey of queryKeys) {
      const query = this.#queryClient
        .getQueryCache()
        .find({ queryKey, exact: true });
      if (!query || query.state.data === undefined) return null;
      if (capturedByHash.has(query.queryHash)) continue;
      const pageParams = getPageParams(query.state.data);
      capturedByHash.set(query.queryHash, {
        queryHash: query.queryHash,
        snapshot: {
          queryKey: cloneValue(query.queryKey),
          dataUpdatedAt: query.state.dataUpdatedAt,
          ...(pageParams ? { pageParams } : {}),
        },
      });
    }
    return [...capturedByHash.values()];
  }

  #snapshotsMatch(entry: RegistryEntry): boolean {
    return entry.capturedQueries.every(({ queryHash, snapshot }) => {
      const query = this.#queryClient
        .getQueryCache()
        .find({ queryKey: snapshot.queryKey, exact: true });
      return (
        query?.queryHash === queryHash &&
        query.state.data !== undefined &&
        query.state.dataUpdatedAt === snapshot.dataUpdatedAt &&
        pageParamsMatch(getPageParams(query.state.data), snapshot.pageParams)
      );
    });
  }

  #entryForScope(input: RetainStreamDatasetInput): RegistryEntry | undefined {
    const userKey = String(input.userId);
    return [...this.#entries.values()].find(
      (entry) =>
        entry.userKey === userKey &&
        entry.dataset.studyGroupId === input.studyGroupId &&
        entry.dataset.scopeKey === input.viewKey,
    );
  }

  #sameGroup(left: RegistryEntry, right: RegistryEntry): boolean {
    return (
      left.userKey === right.userKey &&
      left.dataset.studyGroupId === right.dataset.studyGroupId
    );
  }

  #scopeCountWith(
    candidate: RegistryEntry,
    entries: readonly RegistryEntry[],
  ): number {
    return (
      entries.filter((entry) => this.#sameGroup(entry, candidate)).length + 1
    );
  }

  #globalUniqueCount(
    candidate: RegistryEntry,
    key: "postContentIds" | "commentIds",
    entries: readonly RegistryEntry[],
  ): number {
    const ids = new Set(candidate.dataset[key]);
    for (const entry of entries) {
      for (const id of entry.dataset[key]) ids.add(id);
    }
    return ids.size;
  }

  #fitsWith(
    candidate: RegistryEntry,
    entries: readonly RegistryEntry[],
  ): boolean {
    return (
      this.#scopeCountWith(candidate, entries) <= MAX_SCOPES_PER_GROUP &&
      this.#globalUniqueCount(candidate, "postContentIds", entries) <=
        MAX_POSTS_GLOBAL &&
      this.#globalUniqueCount(candidate, "commentIds", entries) <=
        MAX_COMMENTS_GLOBAL
    );
  }

  #planEvictions(
    candidate: RegistryEntry,
    replaced: RegistryEntry | undefined,
  ): RegistryEntry[] | null {
    const remaining = [...this.#entries.values()].filter(
      (entry) => entry !== replaced,
    );
    const planned: RegistryEntry[] = [];
    while (!this.#fitsWith(candidate, remaining)) {
      const requiresScopeEviction =
        this.#scopeCountWith(candidate, remaining) > MAX_SCOPES_PER_GROUP;
      const next = remaining
        .filter(
          (entry) =>
            !this.#isActive(entry) &&
            (!requiresScopeEviction || this.#sameGroup(entry, candidate)),
        )
        .sort(
          (left, right) =>
            left.dataset.lastAccessedAt - right.dataset.lastAccessedAt,
        )[0];
      if (!next) return null;
      remaining.splice(remaining.indexOf(next), 1);
      planned.push(next);
    }
    return planned;
  }

  #isActive(entry: RegistryEntry): boolean {
    return entry.capturedQueries.some(({ queryHash, snapshot }) => {
      const query = this.#queryClient
        .getQueryCache()
        .find({ queryKey: snapshot.queryKey, exact: true });
      return query?.queryHash === queryHash && query.getObserversCount() > 0;
    });
  }

  #evictEntry(
    entry: RegistryEntry,
    downgradeMetadata: boolean,
    protectedQueryHashes: ReadonlySet<string> = new Set(),
    removeActiveQueries = false,
  ): void {
    this.#entries.delete(entry.dataset.id);
    for (const { queryHash, snapshot } of entry.capturedQueries) {
      const owners = this.#queryOwnership.get(queryHash);
      owners?.delete(entry.dataset.id);
      if (owners && owners.size > 0) continue;
      this.#queryOwnership.delete(queryHash);
      if (protectedQueryHashes.has(queryHash)) continue;

      const query = this.#queryClient
        .getQueryCache()
        .find({ queryKey: snapshot.queryKey, exact: true });
      if (
        query?.queryHash !== queryHash ||
        (!removeActiveQueries && query.getObserversCount() > 0)
      ) {
        continue;
      }
      this.#queryClient.removeQueries({
        queryKey: snapshot.queryKey,
        exact: true,
      });
    }
    if (downgradeMetadata) {
      downgradeViewToRouteOnly(
        entry.dataset.userId,
        entry.dataset.studyGroupId,
        entry.dataset.scopeKey,
        this.#sessionStoreOptions,
      );
    }
  }

  #downgrade(input: RetainStreamDatasetInput): void {
    downgradeViewToRouteOnly(
      input.userId,
      input.studyGroupId,
      input.viewKey,
      this.#sessionStoreOptions,
    );
  }

  #rejectRetention(
    input: RetainStreamDatasetInput,
    previous: RegistryEntry | undefined,
  ): void {
    if (previous) this.#evictEntry(previous, false);
    this.#downgrade(input);
  }
}

export const createRetainedDatasetRegistry = (
  options: RetainedDatasetRegistryOptions,
): RetainedDatasetRegistry => new RetainedDatasetRegistry(options);

const defaultRegistry = createRetainedDatasetRegistry({
  queryClient: applicationQueryClient,
});

setDefaultSessionStoreEvictionHandler((evictions) => {
  defaultRegistry.discardSessionStoreEvictions(evictions);
});

export const retainStreamDataset = (input: RetainStreamDatasetInput) =>
  defaultRegistry.retainStreamDataset(input);

export const resolveRetainedDataset = (datasetId: string) =>
  defaultRegistry.resolveRetainedDataset(datasetId);

export const touchRetainedDataset = (datasetId: string) =>
  defaultRegistry.touchRetainedDataset(datasetId);

export const evictRetainedDataset = (datasetId: string) =>
  defaultRegistry.evictRetainedDataset(datasetId);

export const discardRetainedDataset = (
  datasetId: string,
  options?: DiscardRetainedDatasetOptions,
) => defaultRegistry.discardRetainedDataset(datasetId, options);

export const discardRetainedStreamScope = (
  userId: number | string,
  studyGroupId: number,
  viewKey: StreamViewKey,
  options?: DiscardRetainedDatasetOptions,
) =>
  defaultRegistry.discardRetainedStreamScope(
    userId,
    studyGroupId,
    viewKey,
    options,
  );

export const repairRetainedDatasetAfterValidation = (
  datasetId: string,
  invalidatedPostContentIds: readonly number[],
) =>
  defaultRegistry.repairAfterValidation(datasetId, invalidatedPostContentIds);

export const clearRetainedDatasetsForUser = (userId: number | string) =>
  defaultRegistry.clearRetainedDatasetsForUser(userId);

export const forceClearRetainedDatasetsForUser = (userId: number | string) =>
  defaultRegistry.forceClearRetainedDatasetsForUser(userId);

export const forceClearAllRetainedDatasets = () =>
  defaultRegistry.forceClearAllRetainedDatasets();
